/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { QuestionDataType, QuestionType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface QuestionProps {
  id: string;
  name: string;
  question: string;
  questionType: QuestionType;
  dataType: QuestionDataType | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class QuestionRecord extends Record<QuestionProps>({
  id: null as any,
  name: null as any,
  question: null as any,
  questionType: null as any,
  dataType: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    question: string;
    questionType: QuestionType;
    dataType: QuestionDataType | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): QuestionRecord {
    if (json == null) {
      throw new Error('QuestionRecord.fromJS: parameter cannot be null');
    }
    return new QuestionRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'QuestionJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'QuestionJson.name'),
      question: getFromOrThrow(json, (j) => j.question, 'QuestionJson.question'),
      questionType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'questionType', QuestionType.YesNo),
        'QuestionJson.questionType',
      ),
      dataType: json.dataType,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    question: string;
    questionType: QuestionType;
    dataType: QuestionDataType | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [QuestionRecord, Error[]] {
    if (json == null) {
      throw new Error('QuestionRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new QuestionRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'QuestionJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'QuestionJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      question: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.question, 'QuestionJson.question'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      questionType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'questionType', QuestionType.YesNo),
            'QuestionJson.questionType',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dataType: tryOrDefault(
        () => json.dataType,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: QuestionProps) {
    super(props);
    if (props == null) {
      throw new Error('QuestionRecord constructor parameter cannot be null');
    }
  }
}

export abstract class QuestionViewModelBase implements QuestionProps {
  protected readonly _props: QuestionProps;
  private _json?: QuestionProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get question(): string {
    return this._props.question;
  }
  get questionType(): QuestionType {
    return this._props.questionType;
  }
  get dataType(): QuestionDataType | null {
    return this._props.dataType;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isChoice() {
    return this._props.questionType === QuestionType.Choice;
  }
  public get isFreeText() {
    return this._props.questionType === QuestionType.FreeText;
  }
  public get isYesNo() {
    return this._props.questionType === QuestionType.YesNo;
  }
  public get isBoolean() {
    return this._props.dataType === QuestionDataType.Boolean;
  }
  public get isDecimal() {
    return this._props.dataType === QuestionDataType.Decimal;
  }
  public get isInteger() {
    return this._props.dataType === QuestionDataType.Integer;
  }
  public get isString() {
    return this._props.dataType === QuestionDataType.String;
  }

  public isAnyQuestionType(...questionType: QuestionType[]) {
    return questionType.includes(this._props.questionType);
  }
  public isAnyDataType(...questionDataType: QuestionDataType[]) {
    return this._props.dataType != null && questionDataType.includes(this._props.dataType);
  }

  constructor(props: QuestionProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        question: this._props.question,
        questionType: this._props.questionType,
        dataType: this._props.dataType,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllQuestionEntities = (state: AppState) => state.entityStores.question.entities;

export const selectQuestionById = (id: string) => (state: AppState) =>
  state.entityStores.question.entities.get(id);

export const selectQuestionMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.question.meta.get(id);

export const selectQuestionAndMetadataById = (id: string) =>
  createSelector(selectQuestionById(id), selectQuestionMetadataById(id), (question, meta) => ({
    question,
    meta,
  }));

export function selectQuestionEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllQuestionEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, QuestionRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectQuestionEntitiesByName(name: string) {
  return createSelector(selectAllQuestionEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectQuestionEntitiesByQuestion(question: string) {
  return createSelector(selectAllQuestionEntities, (entities) =>
    entities.filter((e) => e.question === question).valueSeq(),
  );
}

export function selectQuestionEntitiesByQuestionType(questionType: QuestionType) {
  return createSelector(selectAllQuestionEntities, (entities) =>
    entities.filter((e) => e.questionType === questionType).valueSeq(),
  );
}

export function selectQuestionEntitiesByDataType(dataType: QuestionDataType) {
  return createSelector(selectAllQuestionEntities, (entities) =>
    entities.filter((e) => e.dataType === dataType).valueSeq(),
  );
}
//#endregion Selectors
